import { useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { abortController } from '../../utils/abortController';
import { roles } from '../../utils/roles';
import { formatDistanceToNow } from 'date-fns';
import { toZonedTime, format } from 'date-fns-tz';
import { Tooltip } from '@mui/material';

const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

const formatLocalTime = (utcDate) => {
    const parsedDate = new Date(`${utcDate.replace(' ', 'T')}Z`); // ensure UTC parsing
    if (isNaN(parsedDate)) return '';
    const zonedDate = toZonedTime(parsedDate, localTimeZone);
    return format(zonedDate, 'yyyy-MM-dd hh:mm:ss a', { timeZone: localTimeZone });
};

const formatTimeAgo = (utcDate) => {
    const parsedDate = new Date(`${utcDate.replace(' ', 'T')}Z`);
    if (isNaN(parsedDate)) return '';
    const zonedDate = toZonedTime(parsedDate, localTimeZone);
    return formatDistanceToNow(zonedDate, { addSuffix: true });
};


const DocumentCommentsModal = ({
    show,
    onHide,
    rowCommentData,
    currentUser,
    handleRecordStatusChange = () => { },
    setShowAlert,
    setAlertMessage,
    setAlertType,
    triggerSessionExpire,
    logOut
}) => {
    /* AUTHORIZATION VARIABLES */
    const isDocumentTeamRole = currentUser?.role === roles[2];
    const isLCTeamRole = currentUser?.role === roles[3];
    const disableComments = [roles[2],roles[6], roles[7], roles[8], roles[12], roles[15], roles[18], roles[19]].includes(currentUser?.role);

    const [commentsList, setCommentsList] = useState([]);
    const [isFetchingComments, setIsFetchingComments] = useState(false);
    const [isAddingComment, setIsAddingComment] = useState(false);
    const [resolvingCommentId, setResolvingCommentId] = useState(null);
    const commentInputRef = useRef(null);


    // Generate initials for avatar
    const getInitials = (name) => {
        return name
            ?.split(' ')
            ?.map((word) => word[0])
            ?.join('')
            ?.toUpperCase()
            ?.slice(0, 2) || '';
    };
    const fetchComments = async () => {
        setIsFetchingComments(true);
        setCommentsList([]);

        let apiUrl = rowCommentData?.isPFIDocument ? "/api/GetDealPFIDocumentComments" : "/api/GetDocumentComments"

        try {
            const response = await axios({
                method: "post",
                url: apiUrl,
                data: {
                    Deal_PFI_Id: rowCommentData.Deal_PFI_Id,
                    Tranche_Id: rowCommentData.Tranche_Id,
                    COO_ID: rowCommentData.COO_ID,
                    External_File_Id: rowCommentData.External_File_Id,
                    Document_Name: rowCommentData.Document_Name,
                    Revision_Number: rowCommentData.Revision_Number,
                    Document_Type: rowCommentData.Document_Type
                },
                signal: abortController.signal
            });

            setIsFetchingComments(false);
            const { status, data } = response;
            if (status === 200) {
                setCommentsList(data?.data);
            } else {
                setAlertMessage("Failed to fetch comments. Displaying sample data.");
                setAlertType("error");
                setShowAlert(true);
            }
        } catch (error) {
            console.log("Get Document Comments Api: ", error);
            setIsFetchingComments(false);
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
            const status = error?.response?.status;
            if (status === 403) {
                triggerSessionExpire();
            } else {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access."
                        : status === 429
                            ? "Request limit exceeded."
                            : "Failed to fetch comments. Displaying sample data."
                );
                setAlertType("error");
                setShowAlert(true);
                if (status === 429) {
                    setTimeout(logOut, 3000);
                }
            }
        }
    };

    useEffect(() => {
        if (show && rowCommentData) {
            fetchComments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, rowCommentData]);

    const handleSaveComment = async () => {
        const commentText = commentInputRef.current.value.trim();
        if (!commentText) return;

        setIsAddingComment(true);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        const updatedRowCommentData = {
            ...rowCommentData,
            Comment: commentText,
            User_Id: currentUser?.id,
            ...(rowCommentData?.isChangingStatus && rowCommentData?.isPFIDocument ? { notification: 0 } : { notification: 1 })
        };

        let apiUrl = rowCommentData?.isPFIDocument ? "/api/AddDealPFIDocumentComment" : "/api/AddDocumentComment";
        const controller = new AbortController();
        const signal = controller.signal;

        try {
            const response = await axios({
                method: "post",
                url: apiUrl,
                data: updatedRowCommentData,
                signal: signal,
                timeout: 30000
            });

            setIsAddingComment(false);
            const { status } = response;

            if (status === 200) {
                commentInputRef.current.value = "";
                fetchComments();
                if (rowCommentData?.isChangingStatus && typeof handleRecordStatusChange === 'function') {
                    // eslint-disable-next-line no-unused-expressions
                    await handleRecordStatusChange(updatedRowCommentData);
                }
            } else {
                setAlertMessage("An error occurred while processing your request. Please try again later or contact the site administrator.");
                setAlertType("error");
                setShowAlert(true);
            }
        } catch (error) {
            console.log("Set Document Comment Api: ", error);
            setIsAddingComment(false);
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
            const status = error?.response?.status;
            if (status === 403) {
                triggerSessionExpire();
            } else {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access."
                        : status === 429
                            ? "Request limit exceeded."
                            : "An error occurred while processing your request."
                );
                setAlertType("error");
                setShowAlert(true);
                if (status === 429) {
                    setTimeout(logOut, 3000);
                }
            }
        }
    };

    const handleResolveComment = async (commentId) => {
        setResolvingCommentId(commentId);
        setShowAlert(false);
        setAlertMessage("");
        setAlertType("");

        let apiUrl = rowCommentData?.isPFIDocument ? "/api/ResolveDealPFIComment" : "/api/ResolveDocumentComment"

        try {
            const response = await axios({
                method: "post",
                url: apiUrl,
                data: {
                    id: commentId,
                    Resolved: 1,
                    Deal_PFI_Id: rowCommentData.Deal_PFI_Id,
                    Tranche_Id: rowCommentData?.Tranche_Id,
                    Document_Type: rowCommentData.Document_Type,
                    Business_Unit_Id: rowCommentData?.Business_Unit_Id,
                    User_Id: currentUser?.id
                },
                signal: abortController.signal
            });

            setResolvingCommentId(null);
            const { status } = response;
            if (status === 200) {
                fetchComments();
            } else {
                setAlertMessage("Failed to resolve comment. Please try again.");
                setAlertType("error");
                setShowAlert(true);
            }
        } catch (error) {
            console.log("Resolve Document Comment Api: ", error);
            setResolvingCommentId(null);
            if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
            const status = error?.response?.status;
            if (status === 403) {
                triggerSessionExpire();
            } else {
                setAlertMessage(
                    status === 401
                        ? "Unauthorized access."
                        : status === 429
                            ? "Request limit exceeded."
                            : "Failed to resolve comment. Please try again."
                );
                setAlertType("error");
                setShowAlert(true);
                if (status === 429) {
                    setTimeout(logOut, 3000);
                }
            }
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered={true} size="lg">
            <Modal.Header style={{ padding: '5px 16px', borderBottom: '1px solid #ddd' }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontSize: '14px !important',
                        fontWeight: 'bold',
                        color: '#fff',
                        backgroundColor: '#0C969C',
                        width: '100%',
                        textAlign: 'center',
                        py: 1
                    }}
                >
                    Comments
                </Typography>
            </Modal.Header>
            <Modal.Body style={{ height: 'calc(60vh - 150px)', overflowY: 'auto', padding: '12px 16px' }}>
                {isFetchingComments && commentsList?.length === 0 ? (
                    <List disablePadding sx={{ width: '100%' }}>
                        {[...Array(3)].map((_, index) => (
                            <Box key={index}>
                                <ListItem sx={{ padding: '12px 16px' }}>
                                    <ListItemAvatar>
                                        <Skeleton variant="circular" width={32} height={32} sx={{ ml: -0.5, mr: 1 }} />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={<Skeleton variant="text" width="40%" height={16} />}
                                        secondary={
                                            <>
                                                <Skeleton variant="text" width="80%" height={16} />
                                                <Skeleton variant="text" width="20%" height={14} sx={{ mt: 0.5 }} />
                                            </>
                                        }
                                    />
                                </ListItem>
                                <Divider sx={{ borderColor: '#ddd' }} />
                            </Box>
                        ))}
                    </List>
                ) : (
                    <List disablePadding sx={{ width: '100%' }}>
                        {commentsList?.length === 0 ? (
                            <ListItem sx={{ py: 2, textAlign: 'center' }}>
                                <ListItemText
                                    primary="No comments yet"
                                    primaryTypographyProps={{ fontSize: '12px !important', color: 'text.secondary' }}
                                />
                            </ListItem>
                        ) : (
                            commentsList?.sort((a, b) => new Date(b.Commented_At) - new Date(a.Commented_At)).map((comment) => (
                                <Box key={comment.id}>
                                    <ListItem
                                        sx={{
                                            padding: '8px 12px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {/* Left Side */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0 }}>
                                            <ListItemAvatar>
                                                <Avatar
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: '#0C969C',
                                                        color: '#ffffff',
                                                        fontSize: '12px !important',
                                                    }}
                                                >
                                                    {getInitials(`${comment.Commented_By}`)}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontSize: '12px !important',
                                                            fontWeight: 'bold',
                                                            color: 'text.primary'
                                                        }}
                                                    >
                                                        {`${comment.Commented_By}`}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <>
                                                        <Typography
                                                            component="span"
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: '12px !important',
                                                                display: 'block',
                                                                color: 'text.secondary',
                                                                whiteSpace: 'pre-wrap',
                                                                lineHeight: 1.4
                                                            }}
                                                        >
                                                            {comment.Comment}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '11px !important',
                                                                display: 'block',
                                                                color: 'text.disabled',
                                                                mt: 0.5,
                                                                '&:hover': { cursor: 'pointer' }
                                                            }}
                                                        >
                                                            <Tooltip title={formatLocalTime(comment.Commented_At)} arrow>
                                                                <Box component="span" sx={{ fontSize: '11px !important' }}>
                                                                    {formatTimeAgo(comment.Commented_At)}
                                                                </Box>
                                                            </Tooltip>
                                                            {` • From ${comment?.RoleName}`}
                                                        </Typography>
                                                    </>
                                                }
                                            />
                                        </Box>

                                        {/* Right Side: Badge or Button */}
                                        <Box
                                            sx={{
                                                flexShrink: 0,
                                                alignSelf: 'center',
                                                ml: 2
                                            }}
                                        >
                                            {(isDocumentTeamRole || (isLCTeamRole && rowCommentData?.isPFIDocument)) && comment.Resolved === 0 ? (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleResolveComment(comment.id)}
                                                    disabled={resolvingCommentId === comment.id || disableComments}
                                                    sx={{
                                                        '&.Mui-disabled': {
                                                            bgcolor: '#0C969C',
                                                            color: '#fff',
                                                            opacity: 0.6
                                                        },
                                                        width: '100px',
                                                        height: '35px',
                                                    }}
                                                >
                                                    {resolvingCommentId === comment.id ? (
                                                        <CircularProgress size={20} sx={{ color: '#fff' }} />
                                                    ) : (
                                                        'Resolve'
                                                    )}
                                                </Button>
                                            ) : (
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.5,
                                                        py: 0.3,
                                                        fontSize: '12px !important',
                                                        fontStyle: 'italic',
                                                        borderRadius: '12px',
                                                        fontWeight: 500,
                                                        bgcolor: comment.Resolved === 0 ? 'rgba(255, 152, 0, 0.15)' : 'rgba(76, 175, 80, 0.15)',
                                                        color: comment.Resolved === 0 ? '#EF6C00' : '#2E7D32',
                                                        border: `1px solid ${comment.Resolved === 0 ? '#FF9800' : '#4CAF50'}`,
                                                        textAlign: 'center',
                                                        minWidth: '80px'
                                                    }}
                                                >
                                                    {comment.Resolved === 0 ? 'Unresolved' : 'Resolved'}
                                                </Typography>
                                            )}
                                        </Box>
                                    </ListItem>
                                    <Divider sx={{ borderColor: '#ddd' }} />
                                </Box>

                            ))
                        )}
                    </List>
                )}
            </Modal.Body>
            <Modal.Footer
                style={{
                    borderTop: '1px solid #ddd',
                    padding: '12px 16px',
                    backgroundColor: '#f5f5f5',
                    boxShadow: '0px -2px 8px rgba(0,0,0,0.1)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, width: '100%' }} className="form-text-field">
                    <TextField
                        label="Add a new comment"
                        multiline
                        rows={2}
                        inputRef={commentInputRef}
                        variant="outlined"
                        sx={{
                            '& .MuiInputBase-root': {
                                padding: '2px 6px',
                                minHeight: '60px',
                            },
                            '& .MuiInputBase-input': {
                                padding: '2px',
                            },
                            width: '100%',
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <Button variant="outlined" size="small" sx={{ height: '35px' }} onClick={onHide}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleSaveComment}
                            disabled={isAddingComment || disableComments}
                            sx={{
                                '&.Mui-disabled': {
                                    bgcolor: '#0C969C',
                                    color: '#fff',
                                    opacity: 0.6
                                },
                                width: '150px',
                                height: '35px',
                            }}
                        >
                            {isAddingComment ? (
                                <CircularProgress size={20} sx={{ color: '#fff' }} />
                            ) : (
                                'Add Comment'
                            )}
                        </Button>
                    </Box>
                </Box>
            </Modal.Footer>
        </Modal>
    );
};

export default DocumentCommentsModal;